import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Popover,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    Slider,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuIcon from '@mui/icons-material/Menu';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CleaningServicesIcon from '@mui/icons-material/CleaningServicesOutlined';
import WebIcon from '@mui/icons-material/WebOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteForeverIcon from '@mui/icons-material/DeleteForeverOutlined';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweepOutlined';
import SaveIcon from '@mui/icons-material/Save';
import PlaceIcon from '@mui/icons-material/Place';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import VideocamIcon from '@mui/icons-material/Videocam';
import { ScreenComposer } from './composer/ScreenComposer';
import { LiveView } from './liveview/LiveView';
import { Playlist } from '../playlists/Playlists.types';
import { formatDuration } from '../playlists/Playlists.types';
import { screensApi, ScreenUpdatePayload } from './Screens.api';
import {
    COMMAND_LABEL,
    PlaybackLogRow,
    Screen,
    ScreenCommand,
    ScreenCommandType,
    ScreenLogLevel,
    ScreenOrientation,
    cachePercent,
    formatAgo,
    formatBytes,
} from './Screens.types';
import { screenDialogBodySx } from './dialogSx';
import { ScreenLocationDialog, ScreenLocationMap, parseScreenLocation } from './ScreenLocationDialog';
import { ScreenResourcePanel } from './ScreenResourcePanel';

export interface ScreenDetailProps {
    screen: Screen | null;
    playlists: Playlist[];
    onChanged: (screen?: Screen) => void;
    onRemoved: (screenId: string) => void;
    onMessage: (message: string, severity?: 'success' | 'error') => void;
}

const PHYSICAL_ACCESS = 'Physical access to the screen is required if you want to add this screen back to the portal.';

const statusColor = (s: ScreenCommand['status']) => (s === 'done' ? 'success' : s === 'failed' ? 'error' : s === 'delivered' ? 'info' : 'default');

const toolbarButtonSx = { whiteSpace: 'nowrap' } as const;

/** Details and controls for one screen on the manage-screens detail page. */
export const ScreenDetail: React.FC<ScreenDetailProps> = ({ screen, playlists, onChanged, onRemoved, onMessage }) => {
    const theme = useTheme();
    const narrow = useMediaQuery(theme.breakpoints.down('md'));
    const [playbackFiltersOpen, setPlaybackFiltersOpen] = useState(false);
    const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);
    const toolbarMenuButtonRef = useRef<HTMLButtonElement | null>(null);
    const [tab, setTab] = useState(0);
    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [playlistId, setPlaylistId] = useState('');
    const [playlistQuery, setPlaylistQuery] = useState('');
    const [orientation, setOrientation] = useState<ScreenOrientation>('landscape');
    const [volume, setVolume] = useState(100);
    const [audioDevice, setAudioDevice] = useState('');
    const [logLevel, setLogLevel] = useState<ScreenLogLevel>('info');
    const [saving, setSaving] = useState(false);
    const [commands, setCommands] = useState<ScreenCommand[]>([]);
    const [playback, setPlayback] = useState<{ items: PlaybackLogRow[]; total: number; configured?: boolean; message?: string }>({ items: [], total: 0 });
    const [playbackPage, setPlaybackPage] = useState(0);
    const [playbackRowsPerPage, setPlaybackRowsPerPage] = useState(10);
    const playbackPageRef = useRef(0);
    const playbackRowsRef = useRef(10);
    const [reelDraft, setReelDraft] = useState('');
    const [fromDraft, setFromDraft] = useState('');
    const [toDraft, setToDraft] = useState('');
    const [playbackFilter, setPlaybackFilter] = useState<{ reel: string; from: string; to: string }>({ reel: '', from: '', to: '' });
    const playbackFilterRef = useRef(playbackFilter);
    const playbackRequestRef = useRef(0);
    const [exporting, setExporting] = useState(false);
    const [confirm, setConfirm] = useState<ScreenCommandType | 'remove' | 'clear-playback' | null>(null);
    const [factoryName, setFactoryName] = useState('');
    const [actionsOpen, setActionsOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [mapOpen, setMapOpen] = useState(false);
    const [playlistOpen, setPlaylistOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [upgrading, setUpgrading] = useState(false);
    const [volumeAnchor, setVolumeAnchor] = useState<HTMLElement | null>(null);
    const audibleVolume = useRef(100);
    const composerDirty = useRef(false);

    const screenId = screen?._id;

    useEffect(() => {
        if (!screen) return;
        setName(screen.name);
        setLocation(screen.location);
        setPlaylistId(screen.playlistId ?? '');
        setOrientation(screen.settings?.orientation ?? 'landscape');
        setVolume(screen.settings?.volume ?? 100);
        setAudioDevice(screen.settings?.audioDevice || '');
        setLogLevel(screen.settings?.logLevel ?? 'info');
        if ((screen.settings?.volume ?? 100) > 0) audibleVolume.current = screen.settings?.volume ?? 100;
        // Only when another screen is opened, not on every refresh of the same one.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenId]);

    const dayStartIso = (day: string) => {
        if (!day) return '';
        const [year, month, date] = day.split('-').map(Number);
        if (!year || !month || !date) return '';
        return new Date(year, month - 1, date, 0, 0, 0, 0).toISOString();
    };
    const dayEndIso = (day: string) => {
        if (!day) return '';
        const [year, month, date] = day.split('-').map(Number);
        if (!year || !month || !date) return '';
        return new Date(year, month - 1, date, 23, 59, 59, 999).toISOString();
    };

    const loadPlayback = (page: number, rows: number, filter = playbackFilterRef.current) => {
        if (!screenId) return;
        const requestId = ++playbackRequestRef.current;
        screensApi.playback(screenId, {
            limit: rows,
            skip: page * rows,
            reel: filter.reel,
            from: dayStartIso(filter.from),
            to: dayEndIso(filter.to),
        }).then((data) => {
            if (requestId !== playbackRequestRef.current) return;
            if (playbackPageRef.current !== page) return;
            const pages = Math.max(1, Math.ceil((data.total || 0) / rows));
            if (page > pages - 1) {
                const next = pages - 1;
                playbackPageRef.current = next;
                setPlaybackPage(next);
                loadPlayback(next, rows, filter);
                return;
            }
            setPlayback(data);
        }).catch((err) => {
            if (requestId !== playbackRequestRef.current) return;
            setPlayback({
                items: [],
                total: 0,
                configured: false,
                message: err instanceof Error ? err.message : 'Could not load playback logs',
            });
        });
    };

    const applyPlaybackFilter = () => {
        const next = { reel: reelDraft.trim(), from: fromDraft, to: toDraft };
        playbackFilterRef.current = next;
        setPlaybackFilter(next);
        playbackPageRef.current = 0;
        setPlaybackPage(0);
        loadPlayback(0, playbackRowsRef.current, next);
    };

    const clearPlaybackFilter = () => {
        setReelDraft('');
        setFromDraft('');
        setToDraft('');
        applyPlaybackFilterFrom('', '', '');
    };

    const applyPlaybackFilterFrom = (reel: string, from: string, to: string) => {
        const next = { reel: reel.trim(), from, to };
        playbackFilterRef.current = next;
        setPlaybackFilter(next);
        playbackPageRef.current = 0;
        setPlaybackPage(0);
        loadPlayback(0, playbackRowsRef.current, next);
    };

    const exportPlayback = async () => {
        if (!screenId) return;
        const filter = playbackFilterRef.current;
        setExporting(true);
        try {
            const data = await screensApi.playback(screenId, {
                limit: 20000,
                skip: 0,
                reel: filter.reel,
                from: dayStartIso(filter.from),
                to: dayEndIso(filter.to),
                exportAll: true,
            });
            const header = ['Reel', 'Started', 'Duration', 'End'];
            const lines = data.items.map((row) => [
                row.reelName || row.reelId || '',
                row.startedAt ? new Date(row.startedAt).toLocaleString() : '',
                row.endedAt ? formatDuration(row.durationMs) : 'playing',
                row.endReason || '',
            ].map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','));
            const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${screen?.name || 'screen'}-playback.csv`;
            link.click();
            URL.revokeObjectURL(url);
            onMessage(`Exported ${data.items.length} of ${data.total} plays`);
        } catch (err) {
            onMessage(err instanceof Error ? err.message : 'Could not export playback', 'error');
        } finally {
            setExporting(false);
        }
    };

    const loadHistory = () => {
        if (!screenId) return;
        screensApi.commands(screenId).then(setCommands).catch(() => undefined);
        loadPlayback(playbackPageRef.current, playbackRowsRef.current, playbackFilterRef.current);
    };

    useEffect(() => {
        setTab(0);
        playbackPageRef.current = 0;
        setPlaybackPage(0);
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenId]);

    useEffect(() => {
        if (!screenId) return undefined;
        const t = setInterval(loadHistory, 10000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenId]);

    if (!screen) return null;

    const d = screen.deviceStatus;
    const progress = d?.progress;
    const downloadPercent = cachePercent(progress);
    const cacheStale = Boolean(screen.playlistId) && Boolean(d) && (d.cachedPlaylistId !== screen.playlistId || d.cachedRevision !== screen.playlistRevision);
    const reelIsThisPlaylist = Boolean(screen.playlistId) && screen.nowPlaying?.playlistId === screen.playlistId && Boolean(screen.nowPlaying?.name);
    const downloadComplete = d?.syncState === 'cached' && Boolean(screen.playlistId) && !cacheStale && reelIsThisPlaylist;
    const assignedPlaylist = playlists.find((p) => p._id === screen.playlistId);
    const nowPlayingLabel = !screen.playlistId || !downloadComplete ? 'None' : (screen.nowPlaying?.name || 'None');
    const reelsCachedLabel = downloadComplete ? String(assignedPlaylist?.items.length || d?.reelsCount || 0) : '0';
    const cacheChipLabel = !screen.playlistId ? 'None' : downloadComplete ? '100%' : `${downloadPercent}%`;
    const cacheChipColor: 'success' | 'info' | 'error' | 'default' = !screen.playlistId
        ? 'default'
        : d?.syncState === 'error'
            ? 'error'
            : downloadComplete
                ? 'success'
                : 'info';
    const cacheValue = (
        <Chip
            size="small"
            color={cacheChipColor}
            label={cacheChipLabel}
            aria-label={screen.playlistId ? `${cacheChipLabel} of the playlist cached` : 'No playlist cached'}
        />
    );
    const seenAt = screen.lastSeenAt ? new Date(screen.lastSeenAt).getTime() : 0;
    const changedAt = screen.configChangedAt ? new Date(screen.configChangedAt).getTime() : 0;
    const settingsNotPulled = changedAt > 0 && seenAt < changedAt;
    const playlistNotOnScreen = screen.playlistId
        ? !(d?.syncState === 'cached' && d.cachedPlaylistId === screen.playlistId && d.cachedRevision === screen.playlistRevision)
        : Boolean(d?.cachedPlaylistId) || d?.syncState === 'caching';
    const configPending = settingsNotPulled || playlistNotOnScreen || Boolean(screen.upgradePending);
    const syncPill = configPending ? (
        <Chip size="small" color={screen.online ? 'info' : 'warning'} label={screen.online ? 'In progress' : 'Non-synced'} />
    ) : null;
    const diskTotal = d?.diskTotalBytes ?? 0;
    const diskLabel = diskTotal > 0 ? `${formatBytes(d?.diskFreeBytes ?? 0)} free of ${formatBytes(diskTotal)}` : `${formatBytes(d?.diskFreeBytes ?? 0)} free`;
    const upgrades = commands.filter((c) => c.type === 'upgrade').slice(0, 5);
    const playlistMatches = playlists.filter((p) => p.name.toLowerCase().includes(playlistQuery.trim().toLowerCase()));
    const mapPosition = parseScreenLocation(screen.location || '');

    const persist = async (payload: ScreenUpdatePayload, okMessage: string) => {
        setSaving(true);
        try {
            onChanged(await screensApi.update(screen._id, payload));
            onMessage(okMessage);
            return true;
        } catch (err) {
            onMessage(err instanceof Error ? err.message : 'Could not save the screen', 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const send = async (type: ScreenCommandType) => {
        try {
            await screensApi.sendCommand(screen._id, type);
            onMessage(`${COMMAND_LABEL[type]} sent; the screen runs it on its next poll`);
            loadHistory();
            onChanged();
        } catch (err) {
            onMessage(err instanceof Error ? err.message : 'Could not send the command', 'error');
        }
    };

    const remove = async () => {
        try {
            await screensApi.remove(screen._id);
            onMessage(`Screen '${screen.name}' removed`);
            onRemoved(screen._id);
        } catch (err) {
            onMessage(err instanceof Error ? err.message : 'Could not remove the screen', 'error');
        }
    };

    const clearPlaybackLogs = async () => {
        try {
            const result = await screensApi.clearPlayback(screen._id);
            if (result.configured === false) {
                onMessage(result.message || 'Infralytiqs is not configured to capture screen playback events.', 'error');
                return;
            }
            playbackPageRef.current = 0;
            setPlaybackPage(0);
            setPlayback({ items: [], total: 0, configured: true });
            loadPlayback(0, playbackRowsRef.current, playbackFilterRef.current);
            onMessage(`Playback logs for '${screen.name}' cleared`);
        } catch (err) {
            onMessage(err instanceof Error ? err.message : 'Could not clear playback logs', 'error');
        }
    };

    const saveVolume = async (next: number) => {
        const previous = volume;
        setVolume(next);
        if (next > 0) audibleVolume.current = next;
        const ok = await persist({ settings: { volume: next } }, next === 0 ? 'Screen muted' : `Volume set to ${next}%`);
        if (!ok) setVolume(previous);
    };

    const saveAudioDevice = async (next: string) => {
        const previous = audioDevice;
        setAudioDevice(next);
        const name = (screen?.deviceStatus?.audioDevices || []).find((device) => device.id === next)?.name;
        const ok = await persist({ settings: { audioDevice: next } }, next ? `Audio output set to ${name || next}` : 'Audio output set to the system default');
        if (!ok) setAudioDevice(previous);
    };

    const openEdit = () => {
        setName(screen.name);
        setLocation(screen.location || '');
        setOrientation(screen.settings?.orientation ?? 'landscape');
        setLogLevel(screen.settings?.logLevel ?? 'info');
        setEditOpen(true);
    };

    const statusRows: Array<[string, React.ReactNode]> = [
        ['Now playing', nowPlayingLabel],
        ['Playlist', screen.playlistName || 'None'],
        ['Cache', cacheValue],
        ['Reels cached', reelsCachedLabel],
        ['Client version', `${d?.clientVersion || '—'}${screen.upgradePending ? ` → ${screen.targetClientVersion} (${d?.upgradeState || 'pending'})` : ''}`],
        ['Browser', d?.browserState || '—'],
        ['Host', `${d?.hostname || '—'} · ${d?.ip || '—'}`],
        ['OS', `${d?.os || '—'} (${d?.arch || '—'})`],
        ['Resolution', d?.resolution || '—'],
        ['Disk', diskLabel],
        ['Drivers', d?.drivers ? (d.drivers.missing?.length ? `Missing ${d.drivers.missing.join(', ')}` : 'OK') : '—'],
        ['Network', d?.nics?.length ? d.nics.map((nic) => `${nic.name} ${nic.carrier ? 'link up' : 'no cable'}${nic.address ? ` ${nic.address}` : ''}`).join(' · ') : '—'],
        ['Uptime', d?.uptimeSec ? formatDuration(d.uptimeSec * 1000) : '—'],
        ['Registered', screen.registeredAt ? new Date(screen.registeredAt).toLocaleString() : '—'],
        ['Machine id', screen.machineId || '—'],
    ];

    const playCount = playbackFilter.reel || playbackFilter.from || playbackFilter.to
        ? `${playback.total} ${playback.total === 1 ? 'play matches' : 'plays match'} this filter`
        : `${playback.total} ${playback.total === 1 ? 'play' : 'plays'}`;

    const screenToolbarButtons = (fromMenu: boolean) => (
        <>
            <Button variant="contained" color="secondary" size="small" startIcon={<MoreHorizIcon />} sx={toolbarButtonSx} onClick={() => { if (fromMenu) setToolbarMenuOpen(false); setActionsOpen(true); }}>
                Actions
            </Button>
            <Button variant="contained" color="secondary" size="small" startIcon={<EditOutlinedIcon />} sx={toolbarButtonSx} onClick={() => { if (fromMenu) setToolbarMenuOpen(false); openEdit(); }}>
                Edit
            </Button>
            <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
                sx={toolbarButtonSx}
                onClick={(event) => {
                    if (fromMenu) {
                        setToolbarMenuOpen(false);
                        setVolumeAnchor(toolbarMenuButtonRef.current);
                    } else {
                        setVolumeAnchor(event.currentTarget);
                    }
                }}
            >
                Volume
            </Button>
            <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<PlaylistPlayIcon />}
                sx={toolbarButtonSx}
                onClick={() => {
                    if (fromMenu) setToolbarMenuOpen(false);
                    setPlaylistId(screen.playlistId ?? '');
                    setPlaylistQuery('');
                    setPlaylistOpen(true);
                }}
            >
                Playlist
            </Button>
            <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<SettingsBackupRestoreIcon />}
                sx={toolbarButtonSx}
                onClick={() => { if (fromMenu) setToolbarMenuOpen(false); setResetOpen(true); }}
            >
                Reset
            </Button>
        </>
    );

    const playbackFilterFields = (stacked: boolean) => (
        <>
            <TextField
                label="Reel"
                size="small"
                value={reelDraft}
                onChange={(event) => setReelDraft(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') applyPlaybackFilter();
                }}
                placeholder="Reel name"
                fullWidth={stacked}
                sx={stacked ? undefined : { minWidth: 180 }}
            />
            <TextField label="Started from" type="date" size="small" value={fromDraft} onChange={(event) => setFromDraft(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth={stacked} />
            <TextField label="Started to" type="date" size="small" value={toDraft} onChange={(event) => setToDraft(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth={stacked} />
            <Button variant="contained" color="secondary" size="small" onClick={() => { applyPlaybackFilter(); setPlaybackFiltersOpen(false); }}>
                Apply
            </Button>
            <Button variant="outlined" color="secondary" size="small" onClick={() => { clearPlaybackFilter(); setPlaybackFiltersOpen(false); }}>
                Clear
            </Button>
            <Button variant="contained" color="success" size="small" disabled={exporting || playback.configured === false} onClick={() => void exportPlayback()}>
                Export
            </Button>
        </>
    );

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center',
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                }}
            >
                {narrow ? (
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', minHeight: 56 }}>
                        <Stack spacing={0.5} alignItems="flex-start" sx={{ position: 'relative', zIndex: 1, maxWidth: '38%' }}>
                            <Box
                                role="img"
                                aria-label={screen.online ? 'Online' : 'Offline'}
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: screen.online ? 'success.main' : 'error.main',
                                    boxShadow: (t) => `0 0 0 3px ${t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                Last seen {formatAgo(screen.lastSeenAt)}
                            </Typography>
                            {syncPill}
                        </Stack>
                        <Typography
                            variant="subtitle1"
                            align="center"
                            sx={{ position: 'absolute', left: 0, right: 0, fontWeight: 700, px: 9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pointerEvents: 'none' }}
                        >
                            {screen.name}
                        </Typography>
                        <IconButton
                            ref={toolbarMenuButtonRef}
                            aria-label="Screen actions"
                            onClick={() => setToolbarMenuOpen(true)}
                            color="secondary"
                            sx={{ ml: 'auto', position: 'relative', zIndex: 1, border: '1px solid', borderColor: 'divider' }}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>
                ) : (
                    <>
                        <Stack direction="row" spacing={1.25} alignItems="center" useFlexGap flexWrap="wrap" sx={{ minWidth: 0, maxWidth: '100%' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {screen.name}
                            </Typography>
                            <Chip size="small" color={screen.online ? 'success' : 'default'} label={screen.online ? 'Online' : 'Offline'} />
                            {syncPill}
                            <Typography variant="caption" color="text.secondary">
                                Last seen {formatAgo(screen.lastSeenAt)}
                            </Typography>
                            {screen.status === 'factory-reset-pending' && <Chip size="small" color="warning" label="Factory reset pending" />}
                        </Stack>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ ml: 'auto' }}>
                            {screenToolbarButtons(false)}
                        </Stack>
                    </>
                )}
            </Box>
            <Dialog open={narrow && toolbarMenuOpen} onClose={() => setToolbarMenuOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>{screen.name}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                        {screenToolbarButtons(true)}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={() => setToolbarMenuOpen(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ mb: 2, px: 0 }}>
                <Tabs
                    value={tab}
                    onChange={(_e, v) => {
                        if (tab === 3 && composerDirty.current && !window.confirm('The layout has unsaved changes. Leave the Composer without saving?')) return;
                        if (tab === 3) composerDirty.current = false;
                        setTab(v);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        minHeight: 0,
                        p: 0,
                        m: 0,
                        '& .MuiTabs-scroller': { p: 0, m: 0 },
                        '& .MuiTabs-flexContainer': { gap: 0, p: 0, m: 0 },
                        '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
                        '& .MuiTabs-indicator': { backgroundColor: 'secondary.main' },
                    }}
                >
                    {(
                        [
                            {
                                id: 0,
                                label: 'Status',
                                icon: <InfoOutlinedIcon fontSize="small" />,
                                gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                hoverGradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                            },
                            {
                                id: 1,
                                label: 'Playback',
                                icon: <PlayCircleOutlineIcon fontSize="small" />,
                                gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                                hoverGradient: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                            },
                            {
                                id: 2,
                                label: 'Upgrade Audit',
                                icon: <SystemUpdateAltIcon fontSize="small" />,
                                gradient: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)',
                                hoverGradient: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)',
                            },
                            {
                                id: 3,
                                label: 'Composer',
                                icon: <DashboardCustomizeIcon fontSize="small" />,
                                gradient: 'linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)',
                                hoverGradient: 'linear-gradient(135deg, #8e24aa 0%, #7b1fa2 100%)',
                            },
                            {
                                id: 4,
                                label: 'Live View',
                                icon: <VideocamIcon fontSize="small" />,
                                gradient: 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)',
                                hoverGradient: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                            },
                        ] as const
                    ).map((item) => (
                        <Tab
                            key={item.id}
                            value={item.id}
                            iconPosition="start"
                            icon={item.icon}
                            label={item.label}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                mr: 0.5,
                                px: 1.5,
                                minHeight: 40,
                                color: tab === item.id ? 'common.white' : 'text.primary',
                                background: tab === item.id ? item.gradient : 'transparent',
                                '&.Mui-selected': { color: 'common.white' },
                                '&:hover': { background: tab === item.id ? item.hoverGradient : 'action.hover' },
                                '&:first-of-type': { ml: 0 },
                            }}
                        />
                    ))}
                </Tabs>
            </Box>

            {tab === 0 && (
                <>
                    <ScreenResourcePanel samples={screen.resourceSamples || []} networkSpeed={screen.networkSpeed} />
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: mapPosition ? '1fr 1fr' : '1fr' },
                            gap: 2,
                            alignItems: 'stretch',
                        }}
                    >
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            Status
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 160 }}>Field</TableCell>
                                    <TableCell>Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {statusRows.map(([label, value]) => (
                                    <TableRow key={label}>
                                        <TableCell>{label}</TableCell>
                                        <TableCell sx={{ fontFamily: label === 'Machine id' || label === 'Resolution' ? 'monospace' : undefined, wordBreak: 'break-word' }}>{value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {d?.lastError && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                {d.lastError}
                            </Alert>
                        )}
                    </Paper>
                    {mapPosition && (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                Location
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {screen.location}
                            </Typography>
                            <Box sx={{ flex: 1, minHeight: 280, display: 'flex' }}>
                                <ScreenLocationMap position={mapPosition} fill />
                            </Box>
                        </Paper>
                    )}
                    </Box>
                </>
            )}

            {tab === 1 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        Playback logs
                    </Typography>
                    {playback.configured === false && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {playback.message || 'Infralytiqs is not configured to capture screen playback events.'}
                        </Alert>
                    )}
                    {narrow ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <IconButton aria-label="Playback filters" onClick={() => setPlaybackFiltersOpen(true)}>
                                <MoreVertIcon />
                            </IconButton>
                            <Typography variant="body2" color="text.secondary">
                                {playCount}
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} useFlexGap flexWrap="wrap">
                            {playbackFilterFields(false)}
                            <Typography variant="body2" color="text.secondary">
                                {playCount}
                            </Typography>
                        </Stack>
                    )}
                    <Drawer anchor="bottom" open={narrow && playbackFiltersOpen} onClose={() => setPlaybackFiltersOpen(false)}>
                        <Stack spacing={1.5} sx={{ p: 2, pb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Playback filters
                            </Typography>
                            {playbackFilterFields(true)}
                        </Stack>
                    </Drawer>
                    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 640 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Reel</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>End</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {playback.items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4}>No playback reported yet</TableCell>
                                </TableRow>
                            ) : (
                                playback.items.map((row) => (
                                    <TableRow key={row._id}>
                                        <TableCell>
                                            {row.reelName || row.reelId}{row.version ? ` v${row.version}` : ''}
                                        </TableCell>
                                        <TableCell>{new Date(row.startedAt).toLocaleString()}</TableCell>
                                        <TableCell>{row.endedAt ? formatDuration(row.durationMs) : 'playing'}</TableCell>
                                        <TableCell>{row.endReason || '—'}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={playback.total}
                        page={playbackPage}
                        onPageChange={(_event, page) => {
                            playbackPageRef.current = page;
                            setPlaybackPage(page);
                            loadPlayback(page, playbackRowsPerPage);
                        }}
                        rowsPerPage={playbackRowsPerPage}
                        onRowsPerPageChange={(event) => {
                            const rows = Number(event.target.value);
                            playbackRowsRef.current = rows;
                            playbackPageRef.current = 0;
                            setPlaybackRowsPerPage(rows);
                            setPlaybackPage(0);
                            loadPlayback(0, rows);
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                    />
                </Paper>
            )}

            {tab === 3 && (
                <ScreenComposer
                    screen={screen}
                    onSaved={(updated) => onChanged(updated)}
                    onMessage={onMessage}
                    onDirtyChange={(dirty) => {
                        composerDirty.current = dirty;
                    }}
                />
            )}

            {tab === 4 && <LiveView screen={screen} onMessage={onMessage} />}

            {tab === 2 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        Upgrade audit
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Version</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Sent</TableCell>
                                <TableCell>Result</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {upgrades.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4}>No upgrades yet</TableCell>
                                </TableRow>
                            ) : (
                                upgrades.map((c) => (
                                    <TableRow key={c._id}>
                                        <TableCell>{c.payload?.targetVersion || '—'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" color={statusColor(c.status) as 'success' | 'error' | 'info' | 'default'} label={c.status} />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={`${new Date(c.createdAt).toLocaleString()} by ${c.createdBy}`}>
                                                <span>{formatAgo(c.createdAt)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {c.status === 'done' && c.completedAt ? new Date(c.completedAt).toLocaleString() : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            <Dialog open={actionsOpen} onClose={() => setActionsOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Actions · {screen.name}</DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                        <Button variant="contained" color="secondary" startIcon={<RestartAltIcon />} onClick={() => { setActionsOpen(false); setConfirm('reboot'); }}>
                            Reboot
                        </Button>
                        <Button variant="contained" color="secondary" startIcon={<CleaningServicesIcon />} onClick={() => { setActionsOpen(false); setConfirm('clear-cache'); }}>
                            Clear cache
                        </Button>
                        <Button variant="contained" color="secondary" startIcon={<WebIcon />} onClick={() => { setActionsOpen(false); setConfirm('clear-browser-cache'); }}>
                            Clear browser cache
                        </Button>
                        <Button variant="contained" color="secondary" startIcon={<RefreshIcon />} onClick={() => { setActionsOpen(false); setConfirm('restart-browser'); }}>
                            Restart browser
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<SystemUpdateAltIcon />}
                            disabled={upgrading}
                            onClick={() => {
                                void (async () => {
                                    setUpgrading(true);
                                    try {
                                        const settings = await screensApi.settings();
                                        const version = settings.latestRelease?.version;
                                        if (!version) {
                                            onMessage('No screen client release is published', 'error');
                                            return;
                                        }
                                        if (version === d?.clientVersion) {
                                            onMessage(`This screen is already running ${version}`);
                                            setActionsOpen(false);
                                            return;
                                        }
                                        await screensApi.sendCommand(screen._id, 'upgrade', { targetVersion: version });
                                        onMessage(`Upgrade to ${version} scheduled`);
                                        setActionsOpen(false);
                                        loadHistory();
                                        onChanged();
                                    } catch (err) {
                                        onMessage(err instanceof Error ? err.message : 'Could not schedule the upgrade', 'error');
                                    } finally {
                                        setUpgrading(false);
                                    }
                                })();
                            }}
                        >
                            Upgrade to Latest Version
                        </Button>
                    </Stack>
                    {!screen.online && <Alert severity="info" sx={{ mt: 2 }}>The screen is offline. Commands wait until it polls again.</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={() => setActionsOpen(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editOpen} onClose={saving ? undefined : () => setEditOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit · {screen.name}</DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} size="small" inputProps={{ maxLength: 120 }} />
                        <TextField
                            label="Location"
                            value={location}
                            placeholder="Click to choose on the map"
                            size="small"
                            onClick={() => setMapOpen(true)}
                            InputProps={{ readOnly: true, endAdornment: <PlaceIcon color="action" /> }}
                            helperText="Opens a map. The point you select is stored as the screen's latitude and longitude."
                        />
                        <FormControl size="small">
                            <InputLabel id="screen-orientation">Orientation</InputLabel>
                            <Select labelId="screen-orientation" label="Orientation" value={orientation} onChange={(e) => setOrientation(e.target.value as ScreenOrientation)}>
                                <MenuItem value="landscape">Landscape</MenuItem>
                                <MenuItem value="portrait">Portrait (rotated 90°)</MenuItem>
                                <MenuItem value="landscape-flipped">Landscape (upside down)</MenuItem>
                                <MenuItem value="portrait-flipped">Portrait (rotated 270°)</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small">
                            <InputLabel id="screen-loglevel">Log level</InputLabel>
                            <Select labelId="screen-loglevel" label="Log level" value={logLevel} onChange={(e) => setLogLevel(e.target.value as ScreenLogLevel)}>
                                <MenuItem value="info">info</MenuItem>
                                <MenuItem value="debug">debug</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} disabled={saving} onClick={() => setEditOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<SaveIcon />}
                        disabled={saving || !name.trim()}
                        onClick={() => {
                            void persist({ name: name.trim(), location: location.trim(), settings: { orientation, logLevel } }, 'Screen saved').then((ok) => {
                                if (ok) setEditOpen(false);
                            });
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ScreenLocationDialog
                open={mapOpen}
                value={location}
                onClose={() => setMapOpen(false)}
                onSelect={(next) => {
                    setLocation(next);
                    setMapOpen(false);
                }}
            />

            <Popover
                open={Boolean(volumeAnchor)}
                anchorEl={volumeAnchor}
                onClose={() => setVolumeAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ p: 2, width: 320 }}>
                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="audio-output">Audio output</InputLabel>
                        <Select
                            labelId="audio-output"
                            label="Audio output"
                            value={audioDevice}
                            disabled={saving}
                            onChange={(event) => void saveAudioDevice(event.target.value)}
                        >
                            <MenuItem value="">System default</MenuItem>
                            {(screen.deviceStatus?.audioDevices || []).map((device) => (
                                <MenuItem key={device.id} value={device.id}>{device.name}</MenuItem>
                            ))}
                            {audioDevice && !(screen.deviceStatus?.audioDevices || []).some((device) => device.id === audioDevice) && (
                                <MenuItem value={audioDevice}>{audioDevice}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    {(screen.deviceStatus?.audioDevices || []).length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            This screen has not reported a sound output yet.
                        </Typography>
                    )}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2">{volume === 0 ? 'Muted' : `Volume ${volume}%`}</Typography>
                        <Button
                            size="small"
                            variant="contained"
                            color={volume === 0 ? 'secondary' : 'warning'}
                            startIcon={volume === 0 ? <VolumeUpIcon /> : <VolumeOffIcon />}
                            disabled={saving}
                            onClick={() => void saveVolume(volume === 0 ? audibleVolume.current || 100 : 0)}
                        >
                            {volume === 0 ? 'Unmute' : 'Mute'}
                        </Button>
                    </Stack>
                    <Slider
                        size="small"
                        color="secondary"
                        value={volume}
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                        onChange={(_e, v) => setVolume(v as number)}
                        onChangeCommitted={(_e, v) => void saveVolume(v as number)}
                    />
                </Box>
            </Popover>

            <Dialog open={playlistOpen} onClose={() => setPlaylistOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Playlist · {screen.name}</DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <TextField label="Search playlist" size="small" fullWidth value={playlistQuery} onChange={(e) => setPlaylistQuery(e.target.value)} sx={{ mb: 1, mt: 0.5 }} />
                    <List dense sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <ListItemButton selected={playlistId === ''} onClick={() => setPlaylistId('')}>
                            <ListItemText primary="No playlist" secondary="The screen shows the waiting page" />
                        </ListItemButton>
                        <Divider />
                        {playlistMatches.length === 0 ? (
                            <ListItemText sx={{ px: 2, py: 1 }} primary="No playlists match your search" />
                        ) : (
                            playlistMatches.map((p) => (
                                <ListItemButton key={p._id} selected={playlistId === p._id} onClick={() => setPlaylistId(p._id)}>
                                    <ListItemText primary={p.name} secondary={`${p.items.length} reels · ${formatDuration(p.totalDurationMs)}`} />
                                </ListItemButton>
                            ))
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={() => setPlaylistOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<SaveIcon />}
                        disabled={saving || playlistId === (screen.playlistId ?? '')}
                        onClick={() => {
                            void persist({ playlistId: playlistId || null }, playlistId ? 'Playlist assigned' : 'Playlist cleared').then((ok) => {
                                if (ok) setPlaylistOpen(false);
                            });
                        }}
                    >
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={resetOpen} onClose={() => setResetOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Reset · {screen.name}</DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<SettingsBackupRestoreIcon />}
                            onClick={() => {
                                setResetOpen(false);
                                setFactoryName('');
                                setConfirm('factory-reset');
                            }}
                        >
                            Factory reset
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteForeverIcon />}
                            onClick={() => {
                                setResetOpen(false);
                                setConfirm('remove');
                            }}
                        >
                            Delete screen
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteSweepIcon />}
                            onClick={() => {
                                setResetOpen(false);
                                setConfirm('clear-playback');
                            }}
                        >
                            Clear playback logs
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={() => setResetOpen(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {confirm === 'remove'
                        ? 'Delete this screen?'
                        : confirm === 'clear-playback'
                          ? `Clear playback logs for '${screen.name}'?`
                          : confirm
                            ? `${COMMAND_LABEL[confirm]} '${screen.name}'?`
                            : ''}
                </DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    {(confirm === 'factory-reset' || confirm === 'remove') && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {PHYSICAL_ACCESS}
                        </Alert>
                    )}
                    <DialogContentText>
                        {confirm === 'reboot' && 'The device restarts and resumes the cached playlist.'}
                        {confirm === 'clear-cache' && 'Cached reels, media and the Chromium cache are deleted. The screen shows a waiting page until it has downloaded the playlist again.'}
                        {confirm === 'clear-browser-cache' && 'Chromium is stopped, its caches are deleted and it is started again. Cached reels are kept.'}
                        {confirm === 'restart-browser' && 'Chromium is restarted without touching any cache.'}
                        {confirm === 'factory-reset' && 'The device deletes its registration, cache and logs, leaves this company and reboots to the setup page. Only the installed screen client remains.'}
                        {confirm === 'remove' && 'Deletes this screen from the company. Use this when the machine has been replaced and will not come back. The record and its token are removed. A device that still polls resets itself to the registration page.'}
                        {confirm === 'clear-playback' && 'Every play recorded for this screen is deleted from ClickHouse. The screen, its playlist and its upgrade history stay. This cannot be undone, and new plays will start recording again.'}
                    </DialogContentText>
                    {confirm === 'factory-reset' && (
                        <TextField sx={{ mt: 2 }} fullWidth size="small" label={`Type "${screen.name}" to confirm`} value={factoryName} onChange={(e) => setFactoryName(e.target.value)} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={() => setConfirm(null)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={confirm === 'factory-reset' || confirm === 'remove' || confirm === 'clear-playback' ? 'error' : 'secondary'}
                        disabled={confirm === 'factory-reset' && factoryName !== screen.name}
                        onClick={() => {
                            const action = confirm;
                            setConfirm(null);
                            if (action === 'remove') void remove();
                            else if (action === 'clear-playback') void clearPlaybackLogs();
                            else if (action) void send(action);
                        }}
                    >
                        {confirm === 'remove' ? 'Delete screen' : confirm === 'clear-playback' ? 'Clear playback logs' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ScreenDetail;
